import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  decidePriceRequest,
  getModerationQueue,
  resolveAutoPausedAd,
} from "@/lib/moderation-admin.functions";

export const Route = createFileRoute("/_authenticated/admin/moderering")({
  component: ModerationPage,
  head: () => ({
    meta: [
      { title: "Moderering – Sellavo admin" },
      { name: "description", content: "Hantera prisbegäran och pausade prylar." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const kr = (n: number) => `${n.toLocaleString("sv-SE")} kr`;

function ModerationPage() {
  const queue = useServerFn(getModerationQueue);
  const decide = useServerFn(decidePriceRequest);
  const resolve = useServerFn(resolveAutoPausedAd);
  const qc = useQueryClient();
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "moderation"],
    queryFn: () => queue({}),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "moderation"] });

  const decideMutation = useMutation({
    mutationFn: (vars: { id: string; approve: boolean; note?: string }) => decide({ data: vars }),
    onSuccess: (_r, vars) => {
      toast.success(vars.approve ? "Priset godkändes." : "Begäran avslogs.");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resolveMutation = useMutation({
    mutationFn: (vars: { adId: string; action: "release" | "remove" }) => resolve({ data: vars }),
    onSuccess: () => {
      toast.success("Klart.");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <AdminShell title="Moderering">
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Prisbegäran{" "}
              <Badge variant="secondary">{data?.requests.length ?? 0}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {isLoading && <p className="text-sm text-muted-foreground">Hämtar …</p>}
            {!isLoading && !data?.requests.length && (
              <p className="text-sm text-muted-foreground">Inget väntar just nu.</p>
            )}
            {data?.requests.map((r) => (
              <div key={r.id} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <Link to="/a/$slug" params={{ slug: r.slug }} className="font-medium underline">
                    {r.title}
                  </Link>
                  <span className="text-sm font-semibold">{kr(r.price)}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Vår bedömning:{" "}
                  {r.aiMin && r.aiMax ? `${kr(r.aiMin)}–${kr(r.aiMax)}` : "saknas"} · {r.email}
                </p>
                {r.motivation && (
                  <p className="mt-2 rounded-md bg-muted p-3 text-sm">{r.motivation}</p>
                )}
                <Textarea
                  className="mt-3"
                  rows={2}
                  placeholder="Meddelande till säljaren (valfritt)"
                  value={notes[r.id] ?? ""}
                  onChange={(e) => setNotes((s) => ({ ...s, [r.id]: e.target.value }))}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={decideMutation.isPending}
                    onClick={() =>
                      decideMutation.mutate({ id: r.id, approve: true, note: notes[r.id] })
                    }
                  >
                    Godkänn priset
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={decideMutation.isPending}
                    onClick={() =>
                      decideMutation.mutate({ id: r.id, approve: false, note: notes[r.id] })
                    }
                  >
                    Avslå
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Automatiskt pausade <Badge variant="secondary">{data?.autoPaused.length ?? 0}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            {!data?.autoPaused.length && (
              <p className="text-sm text-muted-foreground">Inga pausade prylar.</p>
            )}
            {data?.autoPaused.map((a) => (
              <div
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-4"
              >
                <div className="min-w-0">
                  <Link to="/a/$slug" params={{ slug: a.slug }} className="font-medium underline">
                    {a.title}
                  </Link>
                  <p className="text-sm text-muted-foreground">
                    {kr(a.price)} · {a.reason || "Ingen orsak angiven"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resolveMutation.mutate({ adId: a.id, action: "release" })}
                  >
                    Släpp igenom
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => resolveMutation.mutate({ adId: a.id, action: "remove" })}
                  >
                    Ta bort
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}
